import { lazy, useMemo } from 'react';

import classNames from 'classnames';
import dayjs from 'dayjs';
import noop from 'lodash/noop';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { tocCountdownEndDelayMs } from '../../../stores/tournament/constants';
import { EnumOpenDialog } from '../../../stores/ui/types';
import { useStores } from '../../../stores/useStores';

import {
  ChampionsWidgetTrophyCrossfade,
  getChampionsWidgetTrophyFallback,
  resolveChampionsWidgetTrophyPresentation,
} from './championsWidgetTrophy';
import TournamentTimer from './components/TournamentTimer';
import styles from './TournamentWidget.module.scss';

const TrophyIcon = lazy(() => import('../../SVGIcons/TrophyIcon'));
const EXPIRED_COUNTDOWN_DATE = '1970-01-01T00:00:00.000Z';

const TournamentOfChampionsWidget = observer(function TournamentOfChampionsWidget() {
  const { coinTypeStore, uiStore, tournamentStore, tournamentOfChampionsStore } = useStores();
  const { t } = useTranslation();

  const tournament = tournamentStore.currentTournament;
  const {
    tournamentOfChampions,
    isActiveRound,
    isBetweenRounds,
    isSummaryWidget,
    isCountdownToNextRound,
    isInCountdownState,
    countdownEndDate,
    canOpenDialog,
    widgetIcon,
    currentRound,
    isOnFinalRound,
  } = tournamentOfChampionsStore;

  const isCountdownStateButExpired = isInCountdownState && !isCountdownToNextRound;

  const onTimerEnd = () => {
    setTimeout(() => void tournamentOfChampionsStore.refreshStaleData(), tocCountdownEndDelayMs());
  };

  const onClick = () => {
    tournamentStore.handleAnalytics({
      event_action: 'impression',
      params: { triggered_by: 'click', click_location: 'widget' },
    });

    void tournamentStore.fetch();

    if (!canOpenDialog) return;

    uiStore.setOpenDialog({
      dialog: EnumOpenDialog.isOpenTournamentDialog,
      options: { shown_by: 'widget_click' },
    });
  };

  const coinTypeLower = coinTypeStore.coinType.toLowerCase() as 'gc' | 'sc';

  const playerRank = useMemo((): number | null => {
    if (!isActiveRound || !currentRound) return null;

    let playerPosition: number | null = currentRound.playerPosition != null ? currentRound.playerPosition + 1 : null;

    if (playerPosition == null && tournament) {
      const leaderboard = tournament.leaderboards[coinTypeLower];
      if (leaderboard?.player && leaderboard.player.position >= 0) {
        playerPosition = leaderboard.player.position + 1;
      }
    }

    if (playerPosition == null || playerPosition <= 0) return null;
    return playerPosition;
  }, [coinTypeLower, currentRound, isActiveRound, tournament]);

  const hasToc = tournamentOfChampions != null;
  const betweenRoundsWidgetImg = tournamentOfChampions?.assets.betweenRoundsWidgetImg ?? null;
  const currentRoundIndex = tournamentOfChampions?.currentRoundIndex ?? 0;
  const roundsCount = tournamentOfChampions?.rounds.length ?? 0;

  const trophyPresentation = useMemo(() => {
    if (!hasToc) return null;
    return resolveChampionsWidgetTrophyPresentation({
      isCountdown: isInCountdownState,
      isActiveRound,
      isBetweenRounds,
      isSummaryWidget,
      playerRank,
      widgetIcon,
      betweenRoundsWidgetImg,
      currentRoundIndex,
      roundsCount,
      isOnFinalRound,
    });
  }, [
    hasToc,
    isActiveRound,
    isBetweenRounds,
    isInCountdownState,
    isSummaryWidget,
    isOnFinalRound,
    playerRank,
    widgetIcon,
    betweenRoundsWidgetImg,
    currentRoundIndex,
    roundsCount,
  ]);

  if (!tournamentOfChampions) return null;

  const onImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (!trophyPresentation) return;

    const fallback = getChampionsWidgetTrophyFallback(
      trophyPresentation,
      tournamentOfChampions.currentRoundIndex,
      tournamentOfChampions.rounds.length,
    );

    if (e.currentTarget.src !== fallback) {
      e.currentTarget.src = fallback;
    }
  };

  const renderPosition = () => {
    if (playerRank == null) return null;

    const maxDisplay = tournament?.tournamentGroup.maxPositionDisplay ?? 100;
    if (playerRank > maxDisplay) {
      return (
        <div className={styles.championsPosition} data-testid="my-rank">
          {maxDisplay}+
        </div>
      );
    }
    return (
      <div className={styles.championsPosition} data-testid="my-rank">
        #{playerRank}
      </div>
    );
  };

  return (
    <div className="home-widget" data-testid="tournament-widget">
      <div
        className={classNames(styles.tournamentWidget, 'circle-parent')}
        data-testid="champions-tournament-widget-button"
        onClick={onClick}
      >
        <div className={styles.contentWrapper}>
          <div className={styles.outerCircle}>
            <div className={styles.innerCircle} />
          </div>
          {trophyPresentation ? (
            <ChampionsWidgetTrophyCrossfade presentation={trophyPresentation} onImageError={onImageError} />
          ) : (
            <div className={classNames(styles.trophyIconFrame, isInCountdownState && styles.trophyIconFrameCountdown)}>
              <TrophyIcon className={styles.trophyIcon} />
            </div>
          )}
        </div>
        <div className={styles.bottomSection}>
          {isCountdownToNextRound && countdownEndDate && (
            <>
              <div className={styles.startsInLabel} data-testid="starts-in-label">
                {t('tournament.startsIn')}
              </div>
              <TournamentTimer
                isOnWidget
                className="widget-timer"
                endDate={countdownEndDate}
                startDate={dayjs().toISOString()}
                theme="black"
                timerFormat="short"
                to="end"
                onComplete={onTimerEnd}
              />
            </>
          )}
          {isActiveRound && currentRound?.startDate && currentRound?.endDate && (
            <>
              {renderPosition()}
              <TournamentTimer
                isOnWidget
                className="widget-timer"
                endDate={currentRound.endDate}
                startDate={currentRound.startDate}
                theme="black"
                timerFormat="short"
                to="end"
                onComplete={onTimerEnd}
              />
            </>
          )}
          {isCountdownStateButExpired && !isActiveRound && (
            <>
              <div className={styles.startsInLabel} data-testid="starts-in-label">
                {t('tournament.startsIn')}
              </div>
              <TournamentTimer
                isOnWidget
                className="widget-timer"
                endDate={EXPIRED_COUNTDOWN_DATE}
                startDate={EXPIRED_COUNTDOWN_DATE}
                theme="black"
                timerFormat="short"
                to="end"
                onComplete={noop}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default TournamentOfChampionsWidget;
